// @flow

import { all, spawn, put, call, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { downloadFile } from "@sagas/utils";

import hierarchySagas from "./hierarchy";
import absenceReviewSagas from "./absence-review";
import absenceReasonsSagas from "./absence-reasons";
import alertsSagas from "./alerts";
import employeeAbsencesSagas from "./employee-absences";
import employeeNotificationsSagas from "./employee-notifications";
import shiftPatternSagas from "./shift-patterns";
import staffGroupsSagas from "./staff-groups";
import employeePositionsSagas from "./employee-positions";
import specialContactTypes from "./special-contact-types";
import employeeEventsSagas from "./employee-events";
import profileActionCreators from "@modules/profile/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";

import {
  GetEmployeesList,
  GetEmployeePositionsList,
  SaveEmployeeRecordAccess,
  SaveAbsenceRecordAccess,
  SearchNotificationAuditByStatus,
  UpdateNotificationAuditStatus,
  NotificationAuditRemoveAccessToRecord,
  GetWellbeingProviders,
  GetMessagesLoginText,
  GetMessagesReleaseVersion,
  GetReleaseNotesDocument,
} from "@modules/shared/constants/actions";
import { OPERATORS, transformFilters } from "@transformers/request";
import config from "@config";

// Common saga actions
export function* onGetWellbeingProviders(): Generator<*, void, Object> {
  try {
    const response = yield call(
      http.get,
      `api/v2/service-admin/service-config/absence-management/wellbeing/provider`,
    );
    yield put(sharedActionCreators.GetWellbeingProvidersSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetWellbeingProvidersError(e.response));
  }
}

export function* onGetEmployeesList({
  payload,
}: Action<{ clientId: number, page: number, filters: Object }>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const response = yield call(
      http.get,
      `api/v1/employee-admin/client/${payload.clientId}/employee`,
      {
        params: {
          page: payload.page,
          limit: config.resultsPerPage,
          search: transformFilters({ ...payload.filters, is_active: true } || {}, {
            last_name: OPERATORS.LIKE,
            employee_ref_primary: OPERATORS.LIKE,
            date_of_birth: OPERATORS.EQUALS,
          }),
        },
      },
    );
    yield put(sharedActionCreators.GetEmployeesListSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeesListError(e.response));
  }
}

export function* onGetEmployeePositionsList({
  payload,
}: Action<{ clientId: number, employeeId: number, page: number }>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const response = yield call(
      http.get,
      `api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position`,
      {
        params: {
          page: payload.page,
          limit: config.resultsPerPage,
        },
      },
    );
    yield put(sharedActionCreators.GetEmployeePositionsListSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeePositionsListError(e.response));
  }
}

export function* onSaveEmployeeRecordAccess({
  payload,
}: Action<{ clientId: number, employeeId: number }>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-record`,
    );
    yield put(sharedActionCreators.SaveEmployeeRecordAccessSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.SaveEmployeeRecordAccessError(e.response));
  }
}
export function* onSaveAbsenceRecordAccess({
  payload,
}: Action<{ clientId: number, employeeId: number, absenceId: number }>): Generator<
  *,
  void,
  Object,
> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { clientId, employeeId, absenceId } = payload;
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence/${absenceId}/access-record`,
    );
    yield put(sharedActionCreators.SaveEmployeeRecordAccessSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.SaveEmployeeRecordAccessError(e.response));
  }
}
export function* onSearchNotificationAuditByStatus({
  payload,
}: Action<{ managerId: number, clientId: number, status: [string] }>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.managerId || !payload.clientId || !payload.status) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const response = yield call(
      http.get,
      `/api/v1/my-details/client/${payload.clientId}/notification/audit`,
      {
        params: {
          search: transformFilters(
            { status: payload.status, manager_id: payload.managerId },
            {
              status: OPERATORS.EQUALS,
              manager_id: OPERATORS.EQUALS,
            },
          ),
        },
      },
    );
    yield put(sharedActionCreators.SearchNotificationAuditByStatusSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.SearchNotificationAuditByStatusError(e.response));
  }
}

export function* onUpdateNotificationAuditStatus({
  payload,
}: Action<{
  clientId: number,
  auditId: number,
  managerId: number,
  status: "ONGOING" | "COMPLETED",
}>): Generator<*, void, Object> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.auditId ||
      !payload.managerId ||
      !payload.status
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v1/my-details/client/${payload.clientId}/notification/audit/${payload.auditId}`,
      {
        id: payload.auditId,
        manager: {
          id: payload.managerId,
        },
        status: payload.status,
      },
    );
    yield put(
      sharedActionCreators.SearchNotificationAuditByStatus({
        managerId: payload.managerId,
        clientId: payload.clientId,
        status: ["ONGOING", "PENDING"],
      }),
    );
    yield put(profileActionCreators.GetNotificationContacts({}));
    yield put(sharedActionCreators.UpdateNotificationAuditStatusSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.UpdateNotificationAuditStatusError(e.response));
  }
}

export function* onNotificationAuditRemoveAccessToRecord({
  payload,
}: Action<{ clientId: number, auditId: number, individualNotificationId: number }>): Generator<
  *,
  void,
  Object,
> {
  try {
    if (!payload || !payload.clientId || !payload.auditId || !payload.individualNotificationId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { clientId, auditId, individualNotificationId } = payload;

    const response = yield call(
      http.post,
      `/api/v1/my-details/client/${clientId}/notification/audit/${auditId}/action`,
      {
        operation: "DELETE",
        individual_notification_id: individualNotificationId,
        notification_audit_id: auditId,
      },
    );
    yield put(sharedActionCreators.NotificationAuditRemoveAccessToRecordSuccess(response.data));
    yield put(profileActionCreators.GetNotificationContacts({}));
  } catch (e) {
    yield put(sharedActionCreators.NotificationAuditRemoveAccessToRecordError(e.response));
  }
}

function* onGetMessagesLoginText(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `api/v1/system-management/system-message`);

    yield put(sharedActionCreators.GetMessagesLoginTextSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetMessagesLoginTextError(e.response));
  }
}

function* onGetMessagesReleaseVersion(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `api/v1/system-management/release-version`);

    yield put(sharedActionCreators.GetMessagesReleaseVersionSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetMessagesReleaseVersionError(e.response));
  }
}

function* onGetReleaseNotesDocument({
  payload,
}: Action<{ fileName: string }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.fileName) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.get, `api/v1/system-management/release-version/notes`, {
      responseType: "blob",
    });

    downloadFile(response.data, payload.fileName);

    yield put(sharedActionCreators.GetReleaseNotesDocumentSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetReleaseNotesDocumentError(e.response));
  }
}

export function* SharedSaga(): Generator<*, void, void> {
  yield all([
    spawn(hierarchySagas),
    spawn(absenceReviewSagas),
    spawn(absenceReasonsSagas),
    spawn(alertsSagas),
    spawn(employeeAbsencesSagas),
    spawn(employeeNotificationsSagas),
    spawn(shiftPatternSagas),
    spawn(staffGroupsSagas),
    spawn(employeePositionsSagas),
    spawn(specialContactTypes),
    spawn(employeeEventsSagas),
  ]);
  yield takeEvery(GetEmployeesList, onGetEmployeesList);
  yield takeEvery(GetEmployeePositionsList, onGetEmployeePositionsList);
  yield takeEvery(SaveEmployeeRecordAccess, onSaveEmployeeRecordAccess);
  yield takeEvery(SaveAbsenceRecordAccess, onSaveAbsenceRecordAccess);
  yield takeEvery(NotificationAuditRemoveAccessToRecord, onNotificationAuditRemoveAccessToRecord);
  yield takeEvery(SearchNotificationAuditByStatus, onSearchNotificationAuditByStatus);
  yield takeEvery(UpdateNotificationAuditStatus, onUpdateNotificationAuditStatus);
  yield takeEvery(GetWellbeingProviders, onGetWellbeingProviders);
  yield takeEvery(GetMessagesLoginText, onGetMessagesLoginText);
  yield takeEvery(GetMessagesReleaseVersion, onGetMessagesReleaseVersion);
  yield takeEvery(GetReleaseNotesDocument, onGetReleaseNotesDocument);
}
export default SharedSaga;
